import fetch from 'unfetch';
import { loadUiModuleAssets, loadEnvConfig, loadAsset } from './utils/asset-loader';
import { getFluidImports } from './utils/fluid-imports';
import * as assets from './utils/asset-url-service';
import * as eventBus from './utils/event-bus';
import makeManifestData from './utils/manifest-data-constructor';
import { getUiModDetails, getAppShortName, getResponseHeader, checkForPlatformNotifications } from './utils/ui-module-helper';
import { showOverrideFooter } from './utils/override-footer';
import { applyOverrideState } from './utils/override-state';
import handleSetAssetUrl from './utils/set-asset-url-handler';
import { getQueryParamValue, removeQueryParam } from './utils/location-helpers';
import './state-handlers/404';
import { ENV_VAR, ERROR_MSG, ENV_URL, TIMESTAMP } from './utils/constants';
import { _warn, _error, _log, isOfType } from './utils/helpers';

const overrideState = {};
const overrideFooterState = { env: null, assets: {}, buildConfig: null };

// create a function to remove an override
const createAlterFunc = (key, subject = 'OVERRIDE') => ({
	'⚙️  SETTINGS ⚙️': '',
	get [`REMOVE_${subject}`]() {
		window.localStorage.removeItem(key);
		window.location.reload();
		return '...REMOVED!';
	}
});

function getTimestamp() {
    return TIMESTAMP;
}

const sidekickVersion = process.env.SIDEKICK_VERSION ? `${process.env.SIDEKICK_VERSION}/` : '';

// Load the sidekick
const sidekickPromise = (config) => checkForPlatformNotifications().then((notificationConfig) =>
	Promise.all([
		loadAsset('css', `${assets.tools.sidekick}/${sidekickVersion}sidekick.css?ts=${getTimestamp()}`),
		loadAsset('js', `${assets.tools.sidekick}/${sidekickVersion}sidekick.js?ts=${getTimestamp()}`)
	]).then(() => {
		const appName = getAppShortName(window.location);
		const { sidekickConfig } = config;
		window.sidekick.init({
			config: sidekickConfig && sidekickConfig[appName] && { ...sidekickConfig[`${appName}`] },
			addOpenSans: false,
			addSidekickCss: false,
			cleanCache: (/^\/select\/?$/i).test(window.location.pathname),
			notificationConfig:  {
				...notificationConfig,
				notificationBaseUrl:
					isMorpheus && config.endpoint[`${appName}`]
					? config.endpoint[`${appName}`].notifications || ''
					: '',
			},
		});
	})
);

// handle any asset overrides
handleSetAssetUrl();

// Get the environment name from the query string
const envConfig = getQueryParamValue('envConfig');
if (envConfig != null) {
	window.localStorage.setItem(ENV_VAR.DEFAULT, envConfig);
	removeQueryParam('envConfig');
}

// Get the current environment name from localStorage (if previously set)
let envName = window.localStorage.getItem(ENV_VAR.DEFAULT);
// For morpheus if it's not stage and but frontlineeducation domain then it's prod
const isMorphProductionDomain = window.location.host.endsWith('k12.frontlineeducation.com');
// Check if the domain is a production environment
const isIdmProductionDomain = process.env.IDP !== ENV_VAR.MORPHEUS && window.location.host.endsWith('.frontlineeducation.com');
// Check if the environment is "Morpheus" based on the process environment variable
const isMorpheus = process.env.IDP === ENV_VAR.MORPHEUS;
// Get the environment name from the process (if available)
const envNameFromProcess = process.env.ENV_NAME;


// If the environment name is not set, determine the environment based on the domain
if (envName === null) {
	// If the domain is a production environment and not Morpheus, set the environment to "production"
    if ((isIdmProductionDomain && !isMorpheus) || (isMorphProductionDomain && isMorpheus)) {
        envName = ENV_VAR.PRODUCTION;
    } else {
		// Determine the environment based on the host and pathname
        if (window.location.host.startsWith(ENV_URL.SUPERSUITAWSQA)) {
            envName = window.location.pathname.startsWith('/pd-') ? ENV_VAR.PG_QA : ENV_VAR.FC_QA;
        } else if (window.location.host.startsWith(ENV_URL.SUPERSUITAWSDEV)) {
            envName = ENV_VAR.FC_DEV;
        } else {
            envName = ENV_VAR.STAGE;
        }
    }
}

// If the environment is Morpheus and the process environment name is set, use it
if (isMorpheus && envNameFromProcess != null) {
	envName = envNameFromProcess;
}

// Update local storage if the environment name has changed
if (envName && envName !== window.localStorage.getItem(ENV_VAR.DEFAULT)) {
    window.localStorage.setItem(ENV_VAR.DEFAULT, envName);
}

// If the environment name is set, override the environment
if (envName != null && !isIdmProductionDomain) {
	if (!isMorphProductionDomain) {
		overrideFooterState.env = envName;
	}
	overrideState.env = envName;
	const isUrlRe = /^(https?:|\/\/)/;

	const configFile = process.env.IDP !== ENV_VAR.MORPHEUS ? envName : `morpheus-${envName}`;
	const envConfigUrl = (isUrlRe.test(envName) ? envName : `${process.env.BASE_URL}/env-configs/${configFile}.json`);
	// fetch the environment config file
	fetch(envConfigUrl)
		.then(res => res.json())
		.then(envCfg => window.buildConfig = envCfg)
		.then(() => {
			if (isMorpheus) {
				// if we are in morpheus we need to get the response headers
				return getResponseHeader();
			}
		})
		.then((data) => {
			if (isMorpheus) {
				// if we are in morpheus we need to add the headers and isMorpheusLite to the buildConfig
				window.buildConfig = Object.assign({}, window.buildConfig, { headers: data.headers, isMorpheusLite: data.isMorpheusLite });
			}
			_warn(`ENVIRONMENT CONFIG: ${envName}`);
			goBootstrap();
		})
		.catch(err => {
			if (err.message === ERROR_MSG.UNEXP_TOKEN) {
				_error(`MISSING ENVIRONMENT CONFIG: "${envName}" `, createAlterFunc(ENV_VAR.DEFAULT, 'ENVIRONMENT'));
			} else {
				_error(ERROR_MSG.ENV_ERROR, err);
			}
		});
} else {
	// if we are in IDM then no need for response headers
	goBootstrap();
}

function goBootstrap() {
	// store a copy of the original buildConfig
	window.buildConfigOrig = JSON.parse(JSON.stringify(window.buildConfig));

	const buildConfigOverrides = Object.keys(window.localStorage)
		.filter(k => k.startsWith('buildConfig.'));
	overrideFooterState.buildConfig = buildConfigOverrides.length;
	buildConfigOverrides.forEach(envKey => {
		const attrPath = envKey.substr(12).split('.');
		const attrValue = window.localStorage.getItem(envKey);
		overrideState[envKey] = attrValue;
		const overrideResult = attrPath.reduce((obj, key) => {
			if (obj == null) { return null; }
			if (isOfType(obj[key], 'object')) {
				return obj[key];
			} else if (obj[key] != null) {
				obj[key] = attrValue;
				return true;
			} else {
				return null;
			}
		}, window.buildConfig);
		if (overrideResult == null || isOfType(overrideResult, 'object')) {
			_error('INVALID OVERRIDE:', `${envKey} = "${attrValue}"`);
		} else {
			_warn(`${envKey.split('.').join(' 〉')} 〉"${attrValue}" `, createAlterFunc(envKey));
		}
	});
	window.buildConfigData = JSON.parse(JSON.stringify(window.buildConfig));

	const { moduleBasePath, defaultModule , homePageConfig} = window.buildConfig;

	// define SuperSuit global
	window.ss = window.ss || {};
	window.ss.env = Object.assign({}, assets);
	window.ss.events = eventBus;

	// notify developer via console about overridden asset locations
	Object.keys(window.localStorage)
		.filter(k => k.startsWith('assets.'))
		.forEach(envKey => {
			const [assetType, assetKey] = envKey.substr(7).split('.');
			const assetValue = window.localStorage.getItem(envKey);
			_warn(`${assetType} 〉${assetKey} 〉"${assetValue}" `, createAlterFunc(envKey));
			overrideFooterState.assets[assetType] = overrideFooterState.assets[assetType] || 0;
			overrideFooterState.assets[assetType] += 1;
			overrideState[envKey] = assetValue;
		});

	// show the override footer
	showOverrideFooter(overrideFooterState);
	let focused = true;
	window.addEventListener('focus', () => { focused = true; applyOverrideState(overrideState); });
	window.addEventListener('blur', () => focused = false);
	window.addEventListener('contextmenu', () => (!focused && applyOverrideState(overrideState)));
	const killPreLoader = () => window.document.getElementById('pre-loader').style.display = 'none';
	window.ss.util = { killPreLoader, getFluidImports, loadEnvConfig };

	const fetchManifest = async function () {
		const fetchManifestFromUrl = async function (url) {
			const response = await fetch(url);
			return response.json();
		};
		try {
			return await fetchManifestFromUrl(`${moduleBasePath}/manifest.json`);
		} catch (error) {
			_warn(`Failed to fetch manifest from ${moduleBasePath}/manifest.json, falling back to base URL.`);
			window.buildConfig = Object.assign({}, window.buildConfig, { useFallbackLogic: true });
			return await fetchManifestFromUrl(`${process.env.BASE_URL}/manifest.json`);
		}
	};

	// load the sidekick and manifest
	Promise.all([sidekickPromise(window.buildConfig)])
    .then(() => fetchManifest())
    .then(manifestV1 => makeManifestData(manifestV1, assets.uiModules))
    .then(manifest => {
        window.ss.env.manifest = manifest;
        const mod = getUiModDetails();

		// Listen for the sidekick loaded event to perform post-load actions (For testing purposes)
		window.sidekick.events.on('sidekick.loaded', function () {
			_log('sidekick event sidekick.loaded');
		});

        // important auto-redirects
        const loc = window.location;
        const isMorpheusEnv = process.env.IDP === ENV_VAR.MORPHEUS && loc.pathname.endsWith(mod.urlValue);
        const isMatchingPath = loc.pathname.endsWith(`/${mod.urlValue}`) && !loc.pathname.includes('select') && !loc.pathname.includes('auth_callback');
        // default url should be /select/
        // When there is no url then we need this to go to the org switcher and load main.js
        if (mod.urlValue === '' && !isMorpheus) {
            // if the url is empty, redirect to /select/
            loc.pathname = '/select/';
        } else if (mod.urlValue === '' && isMorpheus) {
            const appName = getAppShortName(loc);
            const defaultPath = homePageConfig[appName] && homePageConfig[appName].defaultModule ? `/${homePageConfig[appName].defaultModule}/` : `/${defaultModule}/`;
            loc.pathname = defaultPath;
        } else if ((isMorpheusEnv && isMatchingPath) || isMatchingPath) {
            // if the url is the same as the module, add a trailing
            loc.pathname += '/';
        } else {
            if (!mod.isValid) {
                _warn(`Module [${mod.urlValue}] not found`);
            } else {
                _log(`Hooking up module ${mod.bootstrapName}`);
            }
            return loadUiModuleAssets(mod);
        }
    })
    .then(() => {
        // listen for route changes
        eventBus.on('route-change', (to, from) => {
            const toMod = getUiModDetails(to);
            const fromMod = getUiModDetails(from);
            if (toMod.bootstrapName !== fromMod.bootstrapName) {
                eventBus.fire('ui-module-deactivate', fromMod);
                loadUiModuleAssets(toMod);
            }
        });
    })
    .catch((e) => {
        _error(ERROR_MSG.APP_ERROR, e);
    });

	// listen for the magic key combo to reload the environment config
	window.document.addEventListener('keypress', evt => {
		const { keyCode, which, ctrlKey, shiftKey } = evt;
		const isActionKey = (which === 5 || keyCode === 5);
		const hasModifiers = (ctrlKey && shiftKey);
		if (isActionKey && hasModifiers) { loadEnvConfig(); }
	}, { once: true });

	// fluid-components snooty hookup
	if (window.location.hostname === 'localhost') {
		window.sessionStorage.setItem('snooty', `${process.env.BASE_URL}/fluid-components/snooty.min.js`);
	}
}
